// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { getReferrerAttribution } from "@outschool/attribution";
import {
  OAUTH2_CAN_CREATE_USER,
  OAUTH2_CREATE_OPTIONS,
  OAUTH2_ERROR,
  OAUTH2_IS_NEW_USER,
  OAUTH2_ON_ERROR,
  OAUTH2_ON_SUCCESS,
  OAUTH2_REFRESH_TOKEN,
  OAUTH2_SESSION_TOKEN,
  decodeToken,
  isLearnerAuthTransfer
} from "@outschool/auth-shared";
import { CreateOrLoginError } from "@outschool/gql-backend-generated";
import { useLookupIP } from "@outschool/iplookup-client";
import {
  useLocalStorageReference,
  useLocalStorageState
} from "@outschool/local-storage";
import {
  browseRootPath,
  joinParentAppleAccountPath,
  redirectAfterLoginUrl
} from "@outschool/routes";
import * as Time from "@outschool/time";
import { useAnalytics, useTrackEvent } from "@outschool/ui-analytics";
import {
  SupportedStrategies,
  getAuthStrategy,
  setAuthStrategy
} from "@outschool/ui-auth";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React from "react";
import { useMutation } from "react-apollo";
import { useHistory } from "react-router";

import { LoginErrorOrResultFragment } from "../../shared/client/fragments/LoginErrorOrResultFragment";
import { AppleAuthResponse } from "../../shared/components/appleId/ContinueWithApple";
import {
  LoginOrCreateAccountWithAppleMutation,
  LoginOrCreateAccountWithAppleMutationVariables
} from "../../shared/generated/graphql";
import * as Routes from "../../shared/Routes";
import { useTokenContext } from "../providers/TokenProvider";
import { useAppState } from "../stores/AppStateProvider";

const LoginOrCreateAccountWithApple = gql`
  mutation LoginOrCreateAccountWithApple(
    $code: String!
    $name: String
    $createOptions: CreateAccountWithProviderInput
    $osRef: OsRefInput
  ) {
    loginOrCreateAccountWithApple(
      code: $code
      name: $name
      canCreateAccount: true
      createOptions: $createOptions
      osRef: $osRef
    ) {
      ...LoginErrorOrResultFragment
    }
  }
  ${LoginErrorOrResultFragment}
`;

interface CreateParams {
  isGiftCardSignup?: boolean;
  isLeader?: boolean;
  trackingParameters?: { [key: string]: string };
}

export function useLoginWithApple(
  onSuccess: (
    sessionToken: string,
    postLoginPath: string,
    isLearnerTransfer?: boolean
  ) => void,
  onError: (error: CreateOrLoginError) => void,
  createParams?: CreateParams,
  postLoginPathParam?: string
) {
  const [loginWithApple] = useMutation<
    LoginOrCreateAccountWithAppleMutation,
    LoginOrCreateAccountWithAppleMutationVariables
  >(LoginOrCreateAccountWithApple);
  const appState = useAppState();
  const trackEvent = useTrackEvent();
  const analytics = useAnalytics();

  return React.useCallback(
    async (response: AppleAuthResponse) => {
      const { code } = response.authorization;
      const createOptions = {
        attribution: await analytics.attribution(),
        browserTimeZone: Time.guessBrowserTimeZone(),
        isGiftCardSignup: createParams?.isGiftCardSignup,
        isLeader: createParams?.isLeader
      };
      const name = response.user
        ? `${response.user.name.firstName} ${response.user.name.lastName}`
        : undefined;
      const anonymousId = await analytics.anonymousId();
      const {
        data: { loginOrCreateAccountWithApple: result }
      } = await loginWithApple({
        variables: {
          code,
          name,
          createOptions,
          osRef: {
            ...getReferrerAttribution(),
            anonymousId
          }
        }
      });
      if (result.__typename === "LoginError") {
        onError(result.error);
      } else {
        const {
          isNewUser,
          authentication: { sessionToken }
        } = result;
        if (isNewUser && createParams?.trackingParameters) {
          trackEvent("signup-by-apple", createParams.trackingParameters);
        }
        const decodedToken = decodeToken(sessionToken);
        const isLearnerTransfer = isLearnerAuthTransfer(decodedToken)
          ? decodedToken.isLearnerTransfer
          : false;
        const postLoginPath = isNewUser
          ? joinParentAppleAccountPath()
          : postLoginPathParam || appState.postLoginPath || browseRootPath();
        onSuccess(sessionToken, postLoginPath, isLearnerTransfer);
      }
    },
    [
      analytics,
      appState.postLoginPath,
      createParams?.isGiftCardSignup,
      createParams?.isLeader,
      createParams?.trackingParameters,
      loginWithApple,
      onError,
      onSuccess,
      postLoginPathParam,
      trackEvent
    ]
  );
}

const OAUTH2_LOCAL_STORAGE_TRACKING_PARAMS_KEY = "OAuth2.trackingParameters";

function useOAuth2TrackingParams() {
  const { localStorage, canUseLocalStorage } = useLocalStorageReference();
  const [trackingParameters, setTrackingParameters] = useLocalStorageState<any>(
    OAUTH2_LOCAL_STORAGE_TRACKING_PARAMS_KEY
  );
  const resetTrackingParameters = React.useCallback(() => {
    if (canUseLocalStorage) {
      localStorage.removeItem(OAUTH2_LOCAL_STORAGE_TRACKING_PARAMS_KEY);
    }
    setTrackingParameters(undefined);
  }, [localStorage, canUseLocalStorage, setTrackingParameters]);
  return { trackingParameters, setTrackingParameters, resetTrackingParameters };
}

// Exported here for easier test setup
export const setAuthStrategyWrapped = setAuthStrategy;

export function useLoginWithRedirect(
  onError: (error: CreateOrLoginError) => void,
  createParams?: CreateParams,
  postLoginAction?: string,
  postLoginPath?: string
) {
  const { getTokens } = useTokenContext();
  const appState = useAppState();
  const { setTrackingParameters } = useOAuth2TrackingParams();
  const { ipInfoLoaded, isInGDPR } = useLookupIP();
  const analytics = useAnalytics();

  React.useEffect(() => {
    const authError = Cookies.get(OAUTH2_ERROR);
    if (onError && authError) {
      Cookies.remove(OAUTH2_ERROR);
      onError(authError as CreateOrLoginError);
    }
  }, [onError]);

  React.useEffect(() => {
    async function fn() {
      Cookies.set(OAUTH2_ON_ERROR, window.location.href);
      Cookies.set(OAUTH2_ON_SUCCESS, redirectAfterLoginUrl());
      Cookies.set(OAUTH2_CAN_CREATE_USER, "true");
      Cookies.set(
        OAUTH2_CREATE_OPTIONS,
        JSON.stringify({
          attribution: analytics.attribution(),
          browserTimeZone: Time.guessBrowserTimeZone(),
          isGiftCardSignup: createParams?.isGiftCardSignup,
          isLeader: createParams?.isLeader,
          signup: ipInfoLoaded ? !isInGDPR : false
        })
      );
      const { sessionToken, refreshToken } = getTokens();
      if (sessionToken) {
        Cookies.set(OAUTH2_SESSION_TOKEN, sessionToken);
      } else {
        Cookies.remove(OAUTH2_SESSION_TOKEN);
      }
      if (refreshToken) {
        Cookies.set(OAUTH2_REFRESH_TOKEN, refreshToken);
      } else {
        Cookies.remove(OAUTH2_REFRESH_TOKEN);
      }
      if (createParams?.trackingParameters) {
        setTrackingParameters(createParams?.trackingParameters);
      }
    }
    fn();
  }, [
    analytics,
    appState,
    createParams?.isGiftCardSignup,
    createParams?.isLeader,
    createParams?.trackingParameters,
    setTrackingParameters,
    getTokens,
    ipInfoLoaded,
    isInGDPR
  ]);

  return React.useCallback(
    (strategy: SupportedStrategies) => {
      setAuthStrategyWrapped(
        strategy,
        postLoginAction || appState.postLoginAction,
        postLoginPath || appState.postLoginPath
      );
    },
    [appState, postLoginPath, postLoginAction]
  );
}

export function useTrackAndRedirectAfterLogin() {
  const appState = useAppState();
  const history = useHistory();
  const { trackingParameters, resetTrackingParameters } =
    useOAuth2TrackingParams();
  const trackEvent = useTrackEvent();
  React.useEffect(
    () => {
      const isNewUser = Boolean(Cookies.get(OAUTH2_IS_NEW_USER));
      Cookies.remove(OAUTH2_IS_NEW_USER);
      if (isNewUser) {
        const { strategy } = getAuthStrategy();
        trackEvent(`signup-by-${strategy}`, trackingParameters);
        if (strategy === "facebook") {
          history.push(Routes.joinParentFacebookAccountPath());
        } else if (strategy === "google") {
          history.push(Routes.joinParentGoogleAccountPath());
        } else {
          console.error("Unknown auth strategy:", strategy);
          appState.redirectAfterLogin(browseRootPath());
        }
      } else {
        appState.redirectAfterLogin(browseRootPath());
      }
      resetTrackingParameters();
    },
    // Only run once on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
}
